// 搜索栏组件
// 提供全局搜索和快速筛选功能
import React, { useState, useEffect, useCallback } from 'react';
import {
  Input,
  Space,
  Button,
  Select,
  Tag,
  Tooltip,
  AutoComplete
} from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  ClearOutlined,
  HistoryOutlined
} from '@ant-design/icons';
import { FACTORY_OPTIONS, COST_TYPE_OPTIONS } from '../../config/field-mapping';
import type { FilterCondition } from '../../types/field-mapping';
import AdvancedFilter from './AdvancedFilter';

const { Search } = Input;
const { Option } = Select;

export interface SearchBarProps {
  onSearch?: (searchText: string) => void;
  onFilter?: (filters: FilterCondition[]) => void;
  onClear?: () => void;
  loading?: boolean;
  placeholder?: string;
  showQuickFilters?: boolean;
  searchHistory?: string[];
  onSearchHistoryAdd?: (searchText: string) => void;
}

const SearchBar: React.FC<SearchBarProps> = ({
  onSearch,
  onFilter,
  onClear,
  loading = false,
  placeholder = "搜索产品名称、工厂、产品代码等...",
  showQuickFilters = true,
  searchHistory = [],
  onSearchHistoryAdd
}) => {
  const [searchText, setSearchText] = useState('');
  const [quickFilters, setQuickFilters] = useState<{
    factory?: string;
    costType?: string;
    month?: string;
  }>({});
  const [autoCompleteOptions, setAutoCompleteOptions] = useState<any[]>([]);
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState<FilterCondition[]>([]);

  // 处理搜索
  const handleSearch = useCallback((value: string) => {
    setSearchText(value);
    onSearch?.(value);

    // 添加到搜索历史
    if (value.trim() && onSearchHistoryAdd) {
      onSearchHistoryAdd(value.trim());
    }
  }, [onSearch, onSearchHistoryAdd]);

  // 从状态获取快速筛选条件
  const getQuickFiltersFromState = useCallback((filters: typeof quickFilters): FilterCondition[] => {
    const filterConditions: FilterCondition[] = [];

    if (filters.factory) {
      filterConditions.push({
        field: 'factoryName',
        operator: 'eq',
        value: filters.factory,
        label: `工厂: ${filters.factory}`
      });
    }

    if (filters.costType) {
      filterConditions.push({
        field: 'costType',
        operator: 'eq',
        value: filters.costType,
        label: `成本类型: ${filters.costType}`
      });
    }

    if (filters.month) {
      filterConditions.push({
        field: 'month',
        operator: 'eq',
        value: filters.month,
        label: `月份: ${filters.month}`
      });
    }

    return filterConditions;
  }, []);

  // 处理快速筛选变化
  const handleQuickFilterChange = useCallback((key: string, value: any) => {
    const newFilters = { ...quickFilters, [key]: value };
    setQuickFilters(newFilters);

    // 重新计算所有筛选条件
    const quickFilterConditions = getQuickFiltersFromState(newFilters);
    const allFilters = [...quickFilterConditions, ...advancedFilters];
    onFilter?.(allFilters);
  }, [quickFilters, advancedFilters, getQuickFiltersFromState, onFilter]);

  // 获取快速筛选条件
  const getQuickFilters = useCallback((): FilterCondition[] => {
    const filterConditions: FilterCondition[] = [];

    if (quickFilters.factory) {
      filterConditions.push({
        field: 'factoryName',
        operator: 'eq',
        value: quickFilters.factory,
        label: `工厂: ${quickFilters.factory}`
      });
    }

    if (quickFilters.costType) {
      filterConditions.push({
        field: 'costType',
        operator: 'eq',
        value: quickFilters.costType,
        label: `成本类型: ${quickFilters.costType}`
      });
    }

    if (quickFilters.month) {
      filterConditions.push({
        field: 'month',
        operator: 'eq',
        value: quickFilters.month,
        label: `月份: ${quickFilters.month}`
      });
    }

    return filterConditions;
  }, [quickFilters]);

  // 处理高级筛选
  const handleAdvancedFilter = useCallback((filters: FilterCondition[]) => {
    setAdvancedFilters(filters);

    // 合并快速筛选和高级筛选
    const allFilters = [...getQuickFilters(), ...filters];
    onFilter?.(allFilters);
  }, [getQuickFilters, onFilter]);

  // 清除所有筛选
  const handleClearAll = useCallback(() => {
    setSearchText('');
    setQuickFilters({});
    setAdvancedFilters([]);
    onClear?.();
  }, [onClear]);

  // 生成自动完成选项
  useEffect(() => {
    if (searchText) {
      const options = [
        ...searchHistory
          .filter(item => item.toLowerCase().includes(searchText.toLowerCase()))
          .map(item => ({
            value: item,
            label: (
              <Space>
                <HistoryOutlined style={{ color: '#999' }} />
                <span>{item}</span>
              </Space>
            )
          })),
        // 添加一些常用搜索建议
        ...(searchText.length >= 2 ? [
          {
            value: `工厂:${searchText}`,
            label: `在工厂中搜索 "${searchText}"`
          },
          {
            value: `产品:${searchText}`,
            label: `在产品中搜索 "${searchText}"`
          }
        ] : [])
      ];
      setAutoCompleteOptions(options);
    } else {
      setAutoCompleteOptions([]);
    }
  }, [searchText, searchHistory]);

  // 当前应用的筛选标签
  const appliedFilters = Object.entries(quickFilters)
    .filter(([_, value]) => value)
    .map(([key, value]) => {
      const labels: Record<string, string> = {
        factory: '工厂',
        costType: '成本类型',
        month: '月份'
      };
      return {
        key,
        label: labels[key],
        value
      };
    });

  return (
    <div style={{ marginBottom: 16 }}>
      {/* 主搜索栏 */}
      <div style={{ marginBottom: 12 }}>
        <AutoComplete
          style={{ width: '100%' }}
          options={autoCompleteOptions}
          onSearch={setSearchText}
          onSelect={handleSearch}
          value={searchText}
        >
          <Search
            placeholder={placeholder}
            allowClear
            enterButton={
              <Button 
                type="primary" 
                icon={<SearchOutlined />}
                loading={loading}
              >
                搜索
              </Button>
            }
            size="large"
            onSearch={handleSearch}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </AutoComplete>
      </div>

      {/* 快速筛选 */}
      {showQuickFilters && (
        <div style={{ marginBottom: 12 }}>
          <Space wrap>
            <Select
              placeholder="选择工厂"
              style={{ width: 120 }}
              allowClear
              value={quickFilters.factory}
              onChange={(value) => handleQuickFilterChange('factory', value)}
            >
              {FACTORY_OPTIONS && FACTORY_OPTIONS.length > 0 ? (
                FACTORY_OPTIONS.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))
              ) : (
                <Option disabled>暂无工厂选项</Option>
              )}
            </Select>

            <Select
              placeholder="成本类型"
              style={{ width: 100 }}
              allowClear
              value={quickFilters.costType}
              onChange={(value) => handleQuickFilterChange('costType', value)}
            >
              {COST_TYPE_OPTIONS && COST_TYPE_OPTIONS.length > 0 ? (
                COST_TYPE_OPTIONS.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))
              ) : (
                <Option disabled>暂无成本类型选项</Option>
              )}
            </Select>

            <Select
              placeholder="选择月份"
              style={{ width: 120 }}
              allowClear
              value={quickFilters.month}
              onChange={(value) => handleQuickFilterChange('month', value)}
            >
              <Option value="2024.01">2024.01</Option>
              <Option value="2024.02">2024.02</Option>
              <Option value="2024.03">2024.03</Option>
              <Option value="2024.04">2024.04</Option>
              <Option value="2024.05">2024.05</Option>
              <Option value="2024.06">2024.06</Option>
              <Option value="2024.07">2024.07</Option>
              <Option value="2024.08">2024.08</Option>
              <Option value="2024.09">2024.09</Option>
              <Option value="2024.10">2024.10</Option>
              <Option value="2024.11">2024.11</Option>
              <Option value="2024.12">2024.12</Option>
            </Select>

            <Button
              icon={<FilterOutlined />}
              onClick={() => setShowAdvancedFilter(true)}
              type={advancedFilters.length > 0 ? 'primary' : 'default'}
            >
              高级筛选
              {advancedFilters.length > 0 && (
                <span style={{ marginLeft: 4 }}>({advancedFilters.length})</span>
              )}
            </Button>

            {(searchText || appliedFilters.length > 0 || advancedFilters.length > 0) && (
              <Tooltip title="清除所有筛选条件">
                <Button
                  icon={<ClearOutlined />}
                  onClick={handleClearAll}
                >
                  清除
                </Button>
              </Tooltip>
            )}
          </Space>
        </div>
      )}

      {/* 应用的筛选标签 */}
      {(appliedFilters.length > 0 || advancedFilters.length > 0) && (
        <div>
          <Space wrap>
            <span style={{ color: '#666', fontSize: 12 }}>当前筛选:</span>
            {appliedFilters.map(filter => (
              <Tag
                key={filter.key}
                closable
                onClose={() => handleQuickFilterChange(filter.key, undefined)}
                color="blue"
              >
                {filter.label}: {filter.value}
              </Tag>
            ))}
            {advancedFilters.map((filter, index) => (
              <Tag
                key={`advanced_${index}`}
                closable
                onClose={() => {
                  const newFilters = advancedFilters.filter((_, i) => i !== index);
                  handleAdvancedFilter(newFilters);
                }}
                color="purple"
              >
                {filter.label}
              </Tag>
            ))}
          </Space>
        </div>
      )}

      {/* 高级筛选模态框 */}
      <AdvancedFilter
        visible={showAdvancedFilter}
        onClose={() => setShowAdvancedFilter(false)}
        onApply={handleAdvancedFilter}
        initialFilters={advancedFilters}
      />
    </div>
  );
};

export default SearchBar;
